import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { Modal } from "app/components/Modal";
import { DropdownIcon } from "app/icons";
import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import {
  ADD_CUSTOMER_FIELDS,
  REQUIRED_CUSTOMER_FIELDS,
} from "../../data";
import {
  selectCustomerForm,
  selectCustomerSaving,
  selectIsAddModalOpen,
} from "../../redux/selector";
import { customersActions } from "../../redux/slice";
import {
  CustomerFieldConfig,
  CustomerForm,
  CustomerSelectOption,
} from "../../types";
import { useStyles } from "./styles";

const P = "CUSTOMERS.MODAL";

const AddCustomerModal = () => {
  const { t } = useTranslation();
  const styles = useStyles();
  const dispatch = useDispatch();
  const open = useSelector(selectIsAddModalOpen);
  const form = useSelector(selectCustomerForm);
  const saving = useSelector(selectCustomerSaving);

  const onClose = () => {
    if (saving) return;
    dispatch(customersActions.closeAddModal());
  };

  const setField = (name: keyof CustomerForm, value: string) =>
    dispatch(customersActions.setFormField({ name, value }));

  const placeholder = (field: CustomerFieldConfig) => {
    if (field.placeholder) {
      return t(`${P}.PLACEHOLDERS.${field.placeholder}`);
    }

    return t(
      `${P}.PLACEHOLDERS.${field.type === "select" ? "SELECT" : "ENTER"}_${field.label}`
    );
  };

  const optionLabel = (option: CustomerSelectOption) =>
    option.labelKey ? t(option.labelKey) : option.label || option.value;

  const isValid = REQUIRED_CUSTOMER_FIELDS.every((key) =>
    Boolean(form[key].trim())
  );

  return (
    <Modal open={open} onClose={onClose}>
      <Box className={styles.modal}>
        <Box className={styles.header}>
          <Typography className={styles.title}>{t(`${P}.ADD_TITLE`)}</Typography>
          <IconButton
            className={styles.closeButton}
            onClick={onClose}
            aria-label={t("BUTTONS.CANCEL")}
            disabled={saving}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Box className={styles.body}>
          {ADD_CUSTOMER_FIELDS.map((field) => {
            const { key, label, type, options = [] } = field;

            return (
              <Box key={key} className={styles.field}>
                <Typography className={styles.label}>
                  {t(`${P}.FIELDS.${label}`)}
                </Typography>
                <TextField
                  className={styles.input}
                  fullWidth
                  select={type === "select"}
                  variant="outlined"
                  placeholder={placeholder(field)}
                  value={form[key]}
                  disabled={saving}
                  InputProps={{ notched: false }}
                  SelectProps={
                    type === "select"
                      ? {
                          displayEmpty: true,
                          IconComponent: DropdownIcon,
                          renderValue: (value) => {
                            const selected = options.find(
                              (option) => option.value === value
                            );
                            return selected
                              ? optionLabel(selected)
                              : placeholder(field);
                          },
                        }
                      : undefined
                  }
                  onChange={(event) => setField(key, event.target.value)}
                >
                  {type === "select"
                    ? [
                        <MenuItem key="empty" value="" disabled>
                          {placeholder(field)}
                        </MenuItem>,
                        ...options.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {optionLabel(option)}
                          </MenuItem>
                        )),
                      ]
                    : undefined}
                </TextField>
              </Box>
            );
          })}
        </Box>

        <Box className={styles.footer}>
          <Button
            className={styles.cancelButton}
            variant="outlined"
            onClick={onClose}
            disabled={saving}
          >
            {t("BUTTONS.CANCEL")}
          </Button>
          <Button
            className={styles.addButton}
            variant="contained"
            disableElevation
            disabled={!isValid || saving}
            onClick={() => dispatch(customersActions.addCustomer())}
          >
            {t("BUTTONS.ADD")}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default AddCustomerModal;
